/**
 * MSPDI 列舉數值 <-> domain 型別對照表。
 *
 * 來源：Microsoft Project XML Data Interchange (MSPDI) schema 文件與
 * Project 物件模型（PjTaskLinkType / PjConstraint / PjResourceType ...）。
 *
 * 特別注意 `<PredecessorLink><Type>`：0=FF、1=FS、2=SF、3=SS，
 * 編碼順序與「FS/SS/FF/SF」的直覺列舉順序不同，務必依此表為準。
 */

import type { AccrualType } from '../../domain/task.js';
import type { ConstraintType, TaskType } from '../../domain/task.js';
import type { DependencyType } from '../../domain/dependency.js';
import type { ResourceType } from '../../domain/resource.js';

// ---------------------------------------------------------------------------
// PredecessorLink Type（依賴關係）
// ---------------------------------------------------------------------------

export const DEPENDENCY_TYPE_BY_MSPDI: Readonly<Record<number, DependencyType>> = {
  0: 'FF',
  1: 'FS',
  2: 'SF',
  3: 'SS',
};

export const MSPDI_BY_DEPENDENCY_TYPE: Readonly<Record<DependencyType, number>> = {
  FF: 0,
  FS: 1,
  SF: 2,
  SS: 3,
};

// ---------------------------------------------------------------------------
// ConstraintType
// ---------------------------------------------------------------------------

export const CONSTRAINT_TYPE_BY_MSPDI: Readonly<Record<number, ConstraintType>> = {
  0: 'ASAP',
  1: 'ALAP',
  2: 'MSO',
  3: 'MFO',
  4: 'SNET',
  5: 'SNLT',
  6: 'FNET',
  7: 'FNLT',
};

export const MSPDI_BY_CONSTRAINT_TYPE: Readonly<Record<ConstraintType, number>> = {
  ASAP: 0,
  ALAP: 1,
  MSO: 2,
  MFO: 3,
  SNET: 4,
  SNLT: 5,
  FNET: 6,
  FNLT: 7,
};

// ---------------------------------------------------------------------------
// Task Type
// ---------------------------------------------------------------------------

export const TASK_TYPE_BY_MSPDI: Readonly<Record<number, TaskType>> = {
  0: 'FixedUnits',
  1: 'FixedDuration',
  2: 'FixedWork',
};

export const MSPDI_BY_TASK_TYPE: Readonly<Record<TaskType, number>> = {
  FixedUnits: 0,
  FixedDuration: 1,
  FixedWork: 2,
};

// ---------------------------------------------------------------------------
// Resource Type
// ---------------------------------------------------------------------------

export const RESOURCE_TYPE_BY_MSPDI: Readonly<Record<number, ResourceType>> = {
  0: 'Material',
  1: 'Work',
  2: 'Cost',
};

export const MSPDI_BY_RESOURCE_TYPE: Readonly<Record<ResourceType, number>> = {
  Material: 0,
  Work: 1,
  Cost: 2,
};

// ---------------------------------------------------------------------------
// AccrueAt / FixedCostAccrual
// ---------------------------------------------------------------------------

export const ACCRUAL_TYPE_BY_MSPDI: Readonly<Record<number, AccrualType>> = {
  1: 'Start',
  2: 'Prorated',
  3: 'End',
};

export const MSPDI_BY_ACCRUAL_TYPE: Readonly<Record<AccrualType, number>> = {
  Start: 1,
  Prorated: 2,
  End: 3,
};

// ---------------------------------------------------------------------------
// WeekDay DayType（1=Sunday ... 7=Saturday；0=例外日 TimePeriod）
// ---------------------------------------------------------------------------

/** JS `Date#getDay()`（0=週日）對應的 MSPDI DayType（1=週日）。 */
export const MSPDI_DAY_TYPE_BY_WEEKDAY_INDEX: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

export const WEEKDAY_INDEX_BY_MSPDI_DAY_TYPE: Readonly<Record<number, number>> = {
  1: 0,
  2: 1,
  3: 2,
  4: 3,
  5: 4,
  6: 5,
  7: 6,
};

/** WeekDay 用來表示日曆例外（非固定週期）時的 DayType 值。 */
export const MSPDI_EXCEPTION_DAY_TYPE = 0;

// ---------------------------------------------------------------------------
// PredecessorLink LagFormat（與 Duration/Work 的 DurationFormat 共用同一套 0-21 編碼）
// ---------------------------------------------------------------------------

/**
 * `LagFormat` 中代表「elapsed／日曆時間」的數值（會跨越非工作時間與假日，
 * 例如 `8=ed` 經過日）。已用 MPXJ（社群驗證過的開源 MSPDI 實作）原始碼交叉核對。
 * 未列在這裡（例如 `7=d` 工作日）代表只計工作時間。
 */
export const MSPDI_ELAPSED_LAG_FORMATS: ReadonlySet<number> = new Set([4, 6, 8, 10, 12, 20]);

/** `LagFormat` 中代表「百分比 lag」的數值（前置任務工期的百分比）。 */
export const MSPDI_PERCENT_LAG_FORMATS: ReadonlySet<number> = new Set([19, 20]);

/** 匯出時使用的四種代表性 LagFormat：一般工作時間、elapsed、百分比、elapsed 百分比。 */
export const MSPDI_LAG_FORMAT_WORKING = 7; // d：工作日
export const MSPDI_LAG_FORMAT_ELAPSED = 8; // ed：經過日（elapsed）
export const MSPDI_LAG_FORMAT_PERCENT = 19; // %
export const MSPDI_LAG_FORMAT_ELAPSED_PERCENT = 20; // e%
